/**
 * LeadsListView — Leads dashboard KPIs + filterable table.
 * Click row → opens LeadDetailView (in-place tab swap via onSelect).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Eye, Filter, Plus, RefreshCw, Search, TrendingUp, Users,
} from "lucide-react";
import { API } from "../../lib/api";
import { useDrilldown } from "./DrilldownContext";
import LeadsChartsPanel from "./LeadsChartsPanel";

const STATUS_COLOR = {
  "New Lead": "#1E5AA8", "Cold Prospect": "#6B7A90", "Prospective": "#2E86DE",
  "Hot Prospect": "#F39C12", "New Account Sign": "#27AE60",
  "Existing Account": "#0B2545", "Churn Account": "#8E44AD", "Lost Lead": "#E74C3C",
};

const AGING_LABEL = { green: "Aktif", yellow: "Perlu FU", red: "Stagnan" };

function StatusPill({ status }) {
  const c = STATUS_COLOR[status] || "#6B7A90";
  return (
    <span style={{
      background: c + "22", color: c, border: `1px solid ${c}55`,
      padding: "2px 9px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    }}>{status}</span>
  );
}

function AgingDot({ aging }) {
  if (!aging) return null;
  const map = { green: "#27AE60", yellow: "#F39C12", red: "#E74C3C" };
  return (
    <span title={`${aging.days_since} hari sejak aktivitas terakhir`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, color: "var(--muted)",
      }}>
      <span style={{
        width: 9, height: 9, borderRadius: "50%",
        background: map[aging.color], boxShadow: `0 0 6px ${map[aging.color]}55`,
      }} />
      {AGING_LABEL[aging.color]} · {aging.days_since}d
    </span>
  );
}

export default function LeadsListView({ onCreateNew, onSelectLead }) {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [qualifiedOnly, setQualifiedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const { openDrilldown } = useDrilldown();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (qualifiedOnly) params.qualified = true;
      const [a, b] = await Promise.all([
        axios.get(`${API}/leads`, { params }),
        axios.get(`${API}/leads/dashboard`),
      ]);
      setRows(a.data.rows); setTotal(a.data.total);
      setDash(b.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [skip, limit, search, statusFilter, qualifiedOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [search, statusFilter, qualifiedOnly]);

  return (
    <div data-testid="leads-list-view">
      <div className="topbar-inline">
        <div>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>
            <Users size={20} style={{verticalAlign: "-3px", marginRight: 8}}/> Leads CRM
          </h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            {dash ? `${dash.total} leads · ${dash.qualified} qualified` : "Memuat..."}
            {dash?.scope === "self" && " · scope: leads Anda saja"}
          </p>
        </div>
        <div style={{display: "flex", gap: 8}}>
          <button className="btn btn-ghost" onClick={load}><RefreshCw size={14}/> Refresh</button>
          <button className="btn btn-primary" onClick={onCreateNew} data-testid="btn-create-lead">
            <Plus size={14}/> Generate New Lead
          </button>
        </div>
      </div>

      {dash && (
        <div className="grid kpi-grid" style={{marginTop: 18}}>
          <div className="kpi-card" data-testid="leads-kpi-total" style={{ cursor: "pointer" }}
            onClick={() => openDrilldown({ title: "Semua Lead", metric: "leads_total" })}><div className="kpi-icon"><Users size={20}/></div>
            <div className="label">TOTAL LEADS</div><div className="value">{dash.total.toLocaleString("id-ID")}</div>
            <div className="sub">Seluruh prospect · klik →</div></div>
          <div className="kpi-card teachcast" data-testid="leads-kpi-qualified" style={{ cursor: "pointer" }}
            onClick={() => openDrilldown({ title: "Converted / Qualified Leads", metric: "leads_won" })}><div className="kpi-icon"><CheckCircle2 size={20}/></div>
            <div className="label">QUALIFIED</div><div className="value">{dash.qualified.toLocaleString("id-ID")}</div>
            <div className="sub">Sekolah ≥50 siswa · klik →</div></div>
          <div className="kpi-card eft" style={{ cursor: "pointer" }}
            onClick={() => openDrilldown({ title: "Hot Prospect", metric: "leads_hot_prospect" })}><div className="kpi-icon"><TrendingUp size={20}/></div>
            <div className="label">HOT PROSPECT</div>
            <div className="value">{(dash.pipeline.find((p) => p.status === "Hot Prospect")?.count || 0).toLocaleString("id-ID")}</div>
            <div className="sub">Status Hot Prospect · klik →</div></div>
          <div className="kpi-card sekolah" style={{ cursor: "pointer" }}
            onClick={() => openDrilldown({ title: "New Account Sign", metric: "leads_new_account_sign" })}><div className="kpi-icon"><CheckCircle2 size={20}/></div>
            <div className="label">SIGNED ACCOUNTS</div>
            <div className="value">{(dash.pipeline.find((p) => p.status === "New Account Sign")?.count || 0).toLocaleString("id-ID")}</div>
            <div className="sub">New Account Sign · klik →</div></div>
        </div>
      )}

      {/* Spec-driven Slicer + Charts panel */}
      <LeadsChartsPanel
        onFilterClick={() => { setSearch(""); setStatusFilter(""); setQualifiedOnly(false); }}
        onQualifiedClick={() => { setQualifiedOnly(true); setStatusFilter(""); setSearch(""); }}
      />

      {dash && (
        <div className="card" style={{marginTop: 22}}>
          <div className="card-head">
            <div>
              <h3 className="card-title">Sales Pipeline</h3>
              <p className="card-subtitle">Distribusi lead per status</p>
            </div>
          </div>
          <div style={{display: "flex", flexDirection: "column", gap: 8}}>
            {dash.pipeline.map((p) => {
              const pct = dash.total ? (p.count / dash.total) * 100 : 0;
              const c = STATUS_COLOR[p.status] || "#6B7A90";
              return (
                <div key={p.status} style={{display: "flex", alignItems: "center", gap: 12, cursor: "pointer"}}
                  data-testid={`leads-pipeline-${p.status.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => openDrilldown({ title: `Pipeline · ${p.status}`, metric: "leads_by_status", value: p.status })}>
                  <span style={{minWidth: 150, fontSize: 12, color: "var(--text)", fontWeight: 500}}>{p.status}</span>
                  <div style={{flex: 1, background: "var(--bg-2)", borderRadius: 6, height: 18, position: "relative", overflow: "hidden"}}>
                    <div style={{position: "absolute", inset: 0, width: `${pct}%`, background: `linear-gradient(90deg, ${c}, ${c}cc)`,
                      borderRadius: 6, transition: "width 0.6s cubic-bezier(.2,.8,.3,1)"}} />
                  </div>
                  <span style={{minWidth: 70, textAlign: "right", fontSize: 12, fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "var(--navy)"}}>{p.count} · {pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card" style={{marginTop: 22}}>
        <div className="card-head" style={{flexWrap: "wrap", gap: 12}}>
          <div>
            <h3 className="card-title">Daftar Leads</h3>
            <p className="card-subtitle">{total} lead · klik baris untuk detail</p>
          </div>
          <div style={{display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center"}}>
            <input className="search-input" placeholder="Cari nama, NPSN, alamat..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="leads-search" />
            <select className="select-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="leads-filter-status" style={{minWidth: 160}}>
              <option value="">Semua Status</option>
              {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="chip-check" style={{cursor: "pointer"}}>
              <input type="checkbox" checked={qualifiedOnly} onChange={(e) => setQualifiedOnly(e.target.checked)} />
              <span>Qualified only</span>
            </label>
          </div>
        </div>
        <div style={{overflowX: "auto"}}>
          <table className="tbl" data-testid="leads-table">
            <thead>
              <tr>
                <th>Nama Account</th><th>NPSN</th><th>Status</th>
                <th>Category</th><th>Source</th><th>Sales</th>
                <th>Student</th><th>Estimated Value</th><th>Aging</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="empty">Memuat...</td></tr> :
                rows.length === 0 ? <tr><td colSpan={10} className="empty">Belum ada lead — klik "Generate New Lead" untuk mulai.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} className="row-click" onClick={() => onSelectLead && onSelectLead(r.id)} data-testid={`lead-row-${r.id}`}>
                    <td style={{fontWeight: 600, color: "var(--navy)"}}>{r.nama_account}</td>
                    <td style={{color: "var(--muted)", fontSize: 12}}>{r.npsn || "—"}</td>
                    <td><StatusPill status={r.status_lead} /></td>
                    <td style={{fontSize: 12}}>{r.category}</td>
                    <td style={{fontSize: 12}}>{r.lead_source || "—"}</td>
                    <td style={{fontSize: 12}}>{r.assigned_to}</td>
                    <td style={{textAlign: "right", fontVariantNumeric: "tabular-nums"}}>{r.jumlah_student || "—"}</td>
                    <td style={{textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "var(--good)"}}>
                      {r.estimated_value ? `Rp ${r.estimated_value.toLocaleString("id-ID")}` : "—"}
                    </td>
                    <td><AgingDot aging={r.aging} /></td>
                    <td><Eye size={14} color="var(--accent)" /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div style={{display: "flex", justifyContent: "flex-end", marginTop: 12, gap: 6}}>
          <button className="page-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>← Prev</button>
          <span style={{padding: "0 10px", fontSize: 12}}>Page {Math.floor(skip / limit) + 1} / {Math.ceil(total / limit) || 1}</span>
          <button className="page-btn" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next →</button>
        </div>
      </div>
    </div>
  );
}
